import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SupportButton from "@/components/base/SupportButton";
import { Shield, Lock, Eye, Server, RefreshCw, AlertTriangle, FileCheck } from "lucide-react";

export default function SecurityPage() {
  const [darkMode, setDarkMode] = useState(false);

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className={`min-h-screen ${darkMode ? "bg-slate-950 text-white" : "bg-white text-slate-900"}`}>
        <Navbar darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} />

        <main className="pt-24 pb-16 px-4 md:px-6 max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 rounded-full px-4 py-1.5 mb-4">
              <Shield size={14} className="text-blue-700 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-800 dark:text-blue-300">Seguridad Enterprise</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mb-3">Política de Seguridad</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Última actualización: 1 de junio de 2026</p>
          </div>

          <div className="prose prose-slate dark:prose-invert max-w-none text-slate-600 dark:text-slate-300 space-y-8">
            <section className="bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/40 rounded-2xl p-6">
              <p className="text-sm leading-relaxed mb-0">
                En <strong>emite.do</strong>, la seguridad de los datos fiscales de nuestros clientes es nuestra prioridad
                máxima. Esta política describe los controles y prácticas de seguridad que implementamos para proteger la
                confidencialidad, integridad y disponibilidad de la información, en cumplimiento con los requisitos de la
                **Dirección General de Impuestos Internos (DGII)** para proveedores de servicios de facturación electrónica
                y la **Ley No. 172-13 sobre Protección de Datos de Carácter Personal**.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Lock className="text-blue-800 dark:text-blue-400" size={18} />
                1. Control de Acceso
              </h2>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li><strong>Autenticación segura:</strong> Cada empresa accede al servicio mediante credenciales únicas
                generadas criptográficamente. Las claves se almacenan exclusivamente en forma de hash irreversible,
                garantizando que nunca puedan ser recuperadas ni expuestas.</li>
                <li><strong>Aislamiento de datos:</strong> Los datos de cada empresa están completamente separados e
                aislados. Una empresa nunca puede acceder a documentos, certificados o configuraciones de otra.</li>
                <li><strong>Comunicación entre servicios:</strong> Las comunicaciones internas entre nuestros sistemas
                están protegidas mediante autenticación mutua y secretos compartidos verificados en cada solicitud.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Shield className="text-blue-800 dark:text-blue-400" size={18} />
                2. Encriptación y Protección de Datos
              </h2>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li><strong>Encriptación en reposo:</strong> Los certificados digitales, secretos de integración y datos
                sensibles se protegen mediante cifrado AES-256 con gestión de claves dedicada (Key Management Service).
                Cada dato sensible utiliza una clave de cifrado única que se renueva periódicamente.</li>
                <li><strong>Encriptación en tránsito:</strong> Todas las comunicaciones utilizan HTTPS (TLS 1.2+) tanto
                para la API pública como para las integraciones con la DGII y los webhooks hacia sistemas externos.</li>
                <li><strong>Firma de integridad:</strong> Cada notificación enviada a sistemas externos incluye una firma
                criptográfica (HMAC-SHA256) que permite al receptor verificar la autenticidad del mensaje.</li>
                <li><strong>Almacenamiento inmutable:</strong> Los documentos fiscales (XML firmados, representaciones
                impresas) se almacenan con protección contra modificación y eliminación (retención mínima de 10 años),
                cumpliendo con los requisitos tributarios vigentes.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Eye className="text-blue-800 dark:text-blue-400" size={18} />
                3. Protección de Datos Sensibles
              </h2>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li><strong>Redacción automática:</strong> Todos los registros del sistema pasan por un proceso automático
                de redacción que elimina claves, contraseñas, PINs y certificados. Estos datos sensibles nunca aparecen
                en logs ni sistemas de monitoreo.</li>
                <li><strong>Claves privadas:</strong> Los certificados digitales se cargan en memoria efímera únicamente
                durante el proceso de firma y se purgan inmediatamente después de su uso.</li>
                <li><strong>Descarga segura:</strong> La descarga de artefactos fiscales se realiza mediante enlaces
                temporales con expiración automática, validando la pertenencia del solicitante antes de cada descarga.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <AlertTriangle className="text-blue-800 dark:text-blue-400" size={18} />
                4. Protección contra Ataques
              </h2>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li><strong>Control de tasa de solicitudes:</strong> Se aplican límites estrictos de solicitudes por
                empresa y por tipo de operación para prevenir abusos y ataques de denegación de servicio.</li>
                <li><strong>Circuit breaker:</strong> Cuando los servicios de la DGII experimentan intermitencias, nuestro
                sistema se protege automáticamente evitando saturación y reintentando de forma inteligente cuando
                el servicio se recupera.</li>
                <li><strong>Validación de entrada:</strong> Todos los datos recibidos son validados contra esquemas
                oficiales antes de su procesamiento, rechazando cualquier contenido malformado o no autorizado.</li>
                <li><strong>Gestión de errores:</strong> Los mensajes que no pueden ser procesados tras múltiples intentos
                se enrutan a una cola de revisión dedicada, garantizando que ningún dato se pierda y pueda ser
                recuperado manualmente.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <FileCheck className="text-blue-800 dark:text-blue-400" size={18} />
                5. Auditoría y Trazabilidad
              </h2>
              <p className="text-sm leading-relaxed">
                Cada operación relevante genera un registro de auditoría inmutable que incluye:
              </p>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li>Tipo de evento y nivel de severidad.</li>
                <li>Descripción legible del resultado de la operación.</li>
                <li>Datos estructurados de contexto para investigación.</li>
                <li>Marca temporal precisa de cada acción.</li>
              </ul>
              <p className="text-sm leading-relaxed">
                Adicionalmente, contamos con métricas de rendimiento en tiempo real y monitoreo continuo de la salud
                del sistema.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <RefreshCw className="text-blue-800 dark:text-blue-400" size={18} />
                6. Respaldo y Recuperación
              </h2>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li><strong>Base de datos:</strong> Respaldo continuo con capacidad de recuperación a cualquier punto
                en el tiempo (Point-in-Time Recovery).</li>
                <li><strong>Documentos fiscales:</strong> Almacenamiento con protección contra modificación y
                versionado para garantizar la integridad a largo plazo.</li>
                <li><strong>Colas de mensajes:</strong> Persistencia en disco con cola de errores dedicada para
                garantizar que ningún mensaje se pierda ante fallos.</li>
                <li><strong>Plan de recuperación:</strong> Procedimientos documentados con tiempo objetivo de
                recuperación menor a 30 minutos, validados mediante pruebas periódicas.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Server className="text-blue-800 dark:text-blue-400" size={18} />
                7. Gestión de Incidentes
              </h2>
              <ul className="list-disc pl-5 text-sm space-y-2">
                <li><strong>Detección automática:</strong> Monitoreo continuo de colas de errores y sistemas de alertas
                para detectar incidentes en tiempo real.</li>
                <li><strong>Reconciliación automática:</strong> El sistema detecta y recupera automáticamente documentos
                que queden en estados intermedios, sin intervención manual.</li>
                <li><strong>Comunicación:</strong> Canales de soporte con SLA definidos publicados en nuestro sitio web
                para atender incidentes de forma oportuna.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                8. Revisión Continua
              </h2>
              <p className="text-sm leading-relaxed">
                Esta política se revisa al menos una vez al año o cuando se realizan cambios significativos en la
                infraestructura o los procesos. Los accesos se auditan trimestralmente y las credenciales se rotan
                periódicamente según las mejores prácticas de la industria.
              </p>
            </section>

            <section className="bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/40 rounded-2xl p-6 space-y-3">
              <p className="text-sm leading-relaxed mb-0">
                Este documento público corresponde al <strong>Anexo 1.1 — Política de Seguridad</strong> exigido por la
                DGII para Proveedores de Servicios de Facturación Electrónica.
              </p>
              <ul className="list-disc pl-5 text-sm space-y-1 mb-0">
                <li>
                  Protección de datos (Anexo 1.3):{" "}
                  <a href="/privacy" className="text-blue-800 dark:text-blue-400 hover:underline">
                    https://www.emite.do/privacy
                  </a>
                </li>
                <li>
                  Asistencia y SLA (Anexo 1.4):{" "}
                  <a href="/contacto" className="text-blue-800 dark:text-blue-400 hover:underline">
                    https://www.emite.do/contacto
                  </a>
                </li>
                <li>
                  Contacto / reportes de seguridad:{" "}
                  <a href="mailto:ecf@codec.do" className="text-blue-800 dark:text-blue-400 hover:underline">
                    ecf@codec.do
                  </a>{" "}
                  · +1 (829) 457-0964
                </li>
              </ul>
            </section>
          </div>
        </main>

        <Footer darkMode={darkMode} />
        <SupportButton />
      </div>
    </div>
  );
}
